"use client";

import { forwardRef, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import styles from "./Button.module.css";
import { squircleClipPathData } from "@/app/(app)/editor/[id]/utils/squircle";

const variantClass = {
  primary: styles.variantPrimary,
  attention: styles.variantAttention,
  secondary: styles.variantSecondary,
  tinted: styles.variantTinted,
  ghost: styles.variantGhost,
  chip: styles.variantChip,
  danger: styles.variantDanger,
};

// WCAG-style relative luminance for picking readable text color on a tint.
function tintLuminance(hex) {
  if (typeof hex !== "string") return 0;
  const cleaned = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return 0;
  const channel = (eight) => {
    const v = eight / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const r = channel(parseInt(cleaned.slice(0, 2), 16));
  const g = channel(parseInt(cleaned.slice(2, 4), 16));
  const b = channel(parseInt(cleaned.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const sizeClass = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

const Button = forwardRef(function Button(
  {
    variant = "secondary",
    size = "md",
    iconLeft,
    iconRight,
    iconOnly = false,
    loading = false,
    disabled = false,
    fullWidth = false,
    type = "button",
    href,
    tintColor,
    tintForeground,
    className,
    style,
    children,
    ...rest
  },
  ref
) {
  // The squircle corner is normally drawn natively via CSS `corner-shape`
  // (Chromium 139+). WebKit (iOS Safari AND iOS Chrome — Apple forces every
  // iOS browser onto WebKit) ignores `corner-shape` and falls back to a plain
  // circular `border-radius`, which reads visibly rounder. For the attention
  // variant — the one where the corner silhouette is prominent (gradient + 1px
  // ring) — we reproduce the squircle there by clipping a background layer to a
  // superellipse path. The path is size-dependent, so recompute on resize and
  // inject it as the `--btn-squircle-clip` custom property the CSS consumes.
  // Box-shadow (ring/glow) stays on the element unclipped — it never followed
  // `corner-shape` on any browser, so this matches desktop exactly.
  const innerRef = useRef(null);
  const setRefs = useCallback(
    (node) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  useEffect(() => {
    if (variant !== "attention") return undefined;
    const el = innerRef.current;
    if (!el) return undefined;
    // Native corner-shape support → the browser draws the squircle; do nothing.
    if (
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      CSS.supports("corner-shape", "squircle")
    ) {
      return undefined;
    }
    const apply = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!w || !h) return;
      // Read the resolved circular radius (10/12/13px per size, 14px for lg).
      const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
      const d = squircleClipPathData(w, h, radius);
      if (d) el.style.setProperty("--btn-squircle-clip", `path("${d}")`);
      else el.style.removeProperty("--btn-squircle-clip");
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variant]);

  const classes = [
    styles.base,
    variantClass[variant] ?? variantClass.secondary,
    sizeClass[size] ?? sizeClass.md,
    iconOnly ? styles.iconOnly : "",
    fullWidth ? styles.fullWidth : "",
    loading ? styles.loading : "",
    disabled || loading ? styles.disabled : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  // For variant="tinted", inject the chosen background and a foreground that
  // stays readable on it. Consumer can override the auto-foreground by passing
  // `tintForeground`.
  let resolvedStyle = style;
  if (variant === "tinted" && typeof tintColor === "string" && tintColor) {
    const fg = tintForeground
      ?? (tintLuminance(tintColor) > 0.55 ? "#0f0f0f" : "#ffffff");
    resolvedStyle = {
      ...(style || {}),
      "--btn-tinted-bg": tintColor,
      "--btn-tinted-fg": fg,
    };
  }

  const inner = (
    <>
      {iconLeft ? (
        <span className={styles.icon} aria-hidden="true">
          {iconLeft}
        </span>
      ) : null}
      <span className={styles.content}>{children}</span>
      {iconRight ? (
        <span className={styles.icon} aria-hidden="true">
          {iconRight}
        </span>
      ) : null}
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
    </>
  );

  if (href) {
    return (
      <Link
        ref={setRefs}
        href={href}
        aria-disabled={disabled || loading || undefined}
        className={classes}
        style={resolvedStyle}
        {...rest}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      ref={setRefs}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      style={resolvedStyle}
      {...rest}
    >
      {inner}
    </button>
  );
});

export default Button;
